import { useEffect, useState } from 'react';
import { NAV_LINKS } from '@/mocks/data';

const SECTION_IDS = NAV_LINKS.map((l) => l.key);

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('top');
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-35% 0px -60% 0px' },
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    window.dispatchEvent(new CustomEvent('heigua-search', { detail: kw }));
    setShowSearch(false);
    setKeyword('');
    scrollToId('feed');
  };

  const navTo = (key: string) => {
    setOpen(false);
    if (key === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    scrollToId(key);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] w-full items-center justify-between gap-4 px-4 md:px-6">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            navTo('top');
          }}
          className="flex items-center gap-2.5 whitespace-nowrap cursor-pointer"
          aria-label="51黑瓜速递首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 font-heading text-lg font-black text-background-50 shadow-sm">
            瓜
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-lg font-black text-foreground-950">51黑瓜速递</span>
            <span className="block text-[11px] font-medium tracking-wide text-foreground-500">全网热点 · 抢先看</span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {NAV_LINKS.map((link) => (
            <a
              key={link.key}
              href={`#${link.key}`}
              onClick={(e) => {
                e.preventDefault();
                navTo(link.key);
              }}
              className={`cursor-pointer rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                active === link.key
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className={`hidden items-center overflow-hidden rounded-full border border-background-300 bg-background-50 transition-all duration-300 md:flex ${
              showSearch ? 'w-56 px-1' : 'w-9 px-1'
            }`}
          >
            <button
              type="button"
              onClick={() => setShowSearch((v) => !v)}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-foreground-600 hover:text-primary-600"
              aria-label="展开搜索"
            >
              <i className="ri-search-line text-base"></i>
            </button>
            {showSearch && (
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜热点话题…"
                className="h-8 w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                autoFocus
              />
            )}
          </form>

          <button
            type="button"
            onClick={() => scrollToId('subscribe')}
            className="hidden h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            <i className="ri-notification-3-line text-base"></i>
            订阅快讯
          </button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-800 lg:hidden"
            aria-label="菜单"
          >
            <i className={`${open ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden" aria-label="移动导航">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.key}
                href={`#${link.key}`}
                onClick={(e) => {
                  e.preventDefault();
                  navTo(link.key);
                }}
                className={`cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium ${
                  active === link.key ? 'bg-primary-50 text-primary-700' : 'text-foreground-800 hover:bg-background-100'
                }`}
              >
                {link.label}
              </a>
            ))}
            <form onSubmit={submitSearch} className="mt-2 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜热点话题…"
                className="w-full min-w-0 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </form>
          </div>
        </nav>
      )}
    </header>
  );
}